import * as isNumber from 'lodash/isNumber'
import * as uniqBy from 'lodash/uniqBy'
import { Guards } from 'interfaces/guards'
import { connectLoadable, createParallelGuard } from 'helpers/loadableHelpers'
import { peergroupsGuard, selectedPeergroupTotalsGuard } from 'guards/globalGuards'
import { createSelectedTotalsByTypeSelector, selectedRelationSelector } from 'selectors/peergroupSelectors'
import { RankChart, LineChartInnerOwnProps, LineChartInnerStateProps, Tooltip, TooltipDirection } from 'components/features/pfm/LineChart/LineChart'
import LineChartLoadingOverlay from 'components/atoms/preloaders/LineChartLoadingOverlay'
import { createSelector } from 'reselect'
import { findRank, RANK_CHART_WIDTH, RANK_CHART_DIVIDERS, RANK_CHART_TIPS, RANK_CHART_SECTIONS, percentileToItem } from 'helpers/trendHelpers'
import { createOwnPropsSelector } from 'selectors/globalSelectors'
import { getMonthsCount } from 'helpers/dateTimeHelpers'
import { GenericPage } from 'interfaces/pages'
import { Timeframe } from 'interfaces/chart'
import { formatRank, formatUSCurrency } from 'helpers/formatHelpers'
import { memberGenericTrendsGuard } from 'guards/genericGuards'
import { memberGenericRankCalculationValueSelector } from 'selectors/genericSelectors'
import { createForgottenGuardError } from 'helpers/errorHelpers'
import { DEFAULT_LINE_CHART_SCALES, RANK_CHART_SCALES } from 'constants/chart'
import { isMobileSelector } from 'selectors/viewportSelectors'
import { getTimeframeCopy } from 'helpers/categoriesHelpers'
import { withComparisonSelector } from 'components/features/pageFilters/selectors'

export type GenericRankChartOwnProps = {
  type: GenericPage
  timeframe: Timeframe
}

type OwnProps =
  & GenericRankChartOwnProps
  & LineChartInnerOwnProps

const creditScoreValueDecorator = (value: number) => String(Math.round(value))

const createMapStateToProps = () =>
  createSelector(
    createSelectedTotalsByTypeSelector(),
    memberGenericRankCalculationValueSelector,
    selectedRelationSelector,
    isMobileSelector,
    createOwnPropsSelector<OwnProps>(),
    withComparisonSelector,
    (totals, decimalRankCalculationValue, selectedRelation, isMobile, ownProps, withComparison): LineChartInnerStateProps => {
      if (!isNumber(decimalRankCalculationValue)) {
        throw createForgottenGuardError('GenericRankChartContainer', 'memberGenericTrendsGuard')
      }

      if (!totals) {
        throw createForgottenGuardError('GenericRankChartContainer', 'selectedPeergroupTotalsGuard')
      }

      if (!selectedRelation) {
        throw createForgottenGuardError('GenericRankChartContainer', 'peergroupsGuard')
      }

      const { peerValue: peerTotals, otherValue: otherTotals } = totals
      const { type, timeframe } = ownProps
      const rankCalculationValue = Math.round(decimalRankCalculationValue)
      const peersRank = findRank(peerTotals.percentiles, rankCalculationValue)
      const othersRank = findRank(otherTotals.percentiles, rankCalculationValue)

      // maybe you will need &nbsp; \u00a0
      const w1 = getMonthsCount(timeframe) === 1 ? 'YOUR' : 'YOUR AVG.'
      const w2 = type === 'creditscore' ? 'CREDIT SCORE' : 'NET WORTH'
      const lineTitle = `${ w1 } ${ w2 }`

      // <= is important because when they squash to one that one should be up
      const peersTooltipDirection: TooltipDirection = peersRank <= othersRank ? 'up' : 'down'
      const othersTooltipDirection: TooltipDirection = peersTooltipDirection === 'up' ? 'down' : 'up'
      // squash tooltips if they have same amount
      const defaultTooltips = uniqBy<Tooltip>(
        [
          {
            type: 'peers',
            text: formatRank(peersRank),
            x: peersRank,
            y: rankCalculationValue,
            direction: peersTooltipDirection,
          },
          {
            type: 'others',
            text: formatRank(othersRank),
            x: othersRank,
            y: rankCalculationValue,
            direction: othersTooltipDirection,
          },
        ],
        (tooltip) => tooltip.x
      )


      return {
        memberItems: [],
        peersItems: peerTotals.percentiles.map(percentileToItem),
        otherItems: otherTotals.percentiles.map(percentileToItem),

        dots: [
          {
            x: peersRank,
            y: rankCalculationValue,
            type: 'peers',
            tooltipType: 'peers',
          },
          {
            x: othersRank,
            y: rankCalculationValue,
            type: 'others',
            tooltipType: 'others',
          },
        ],

        limitLine: {
          title: lineTitle,
          amount: rankCalculationValue,
          amountCaption: type === 'creditscore' ? String(rankCalculationValue) : formatUSCurrency(rankCalculationValue),
          kind: 'member',
          labelTheme: type === 'networth' && 'member' || 'default',
        },

        defaultTooltips,

        xWidth: RANK_CHART_WIDTH,

        dividers: RANK_CHART_DIVIDERS,
        headerTips: getTimeframeCopy(timeframe),
        tips: RANK_CHART_TIPS,
        sections: RANK_CHART_SECTIONS,

        scales: type === 'creditscore' ? DEFAULT_LINE_CHART_SCALES : RANK_CHART_SCALES,
        valueDecorator: type === 'creditscore'
          ? creditScoreValueDecorator
          : formatUSCurrency,

        selectedPeergroupName: selectedRelation.name,
        isMobile,

        withComparison,
      }
    }
  )

const trendRankBadgeGuards: Guards<OwnProps> = [
  peergroupsGuard,
  createParallelGuard(
    selectedPeergroupTotalsGuard,
    memberGenericTrendsGuard,
  ),
]

export default connectLoadable<
  LineChartInnerStateProps,
  {},
  LineChartInnerOwnProps,
  OwnProps
>(trendRankBadgeGuards, LineChartLoadingOverlay)(createMapStateToProps)(RankChart)

export const GenericRankChartContainer = connectLoadable<
  LineChartInnerStateProps,
  {},
  LineChartInnerOwnProps,
  OwnProps
>(trendRankBadgeGuards)(createMapStateToProps)(RankChart)
